import { useState, useEffect } from 'react';
import { Phone, Mail, MapPin, Send, CheckCircle, Loader2 } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Contact() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    try {
      await axios.post(`${API}/contact`, formData);
      setIsSuccess(true);
      setFormData({ name: '', email: '', phone: '', message: '' });
    } catch (err) {
      setError('Une erreur est survenue. Veuillez réessayer.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    {
      icon: Phone,
      label: 'Téléphone',
      value: '06 58 93 05 95',
      href: 'tel:0658930595',
    },
    {
      icon: Mail,
      label: 'Email',
      value: 'jehan@0to1-agency.fr',
      href: 'mailto:jehan@0to1-agency.fr',
    },
    {
      icon: MapPin,
      label: 'Localisation',
      value: 'France',
      href: null,
    },
  ];

  return (
    <div className="min-h-screen bg-white pt-24" data-testid="contact-page">
      {/* Hero Section */}
      <section className="py-16 md:py-24 bg-[#ECFDF5]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-3xl">
            <span className="text-[#10B981] text-sm font-semibold uppercase tracking-wider mb-4 block">Contact</span>
            <h1 className="text-4xl md:text-5xl font-bold text-[#1A1A1A] leading-tight mb-6" data-testid="contact-title">
              Parlons de votre <span className="text-[#10B981]">projet</span>
            </h1>
            <p className="text-lg text-[#1E3A5F] font-semibold leading-relaxed">
              Vous souhaitez booster vos ventes ? Remplissez le formulaire ci-dessous et je vous recontacte dans les plus brefs délais.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-20" data-testid="contact-form-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Contact Form */}
            <div className="bg-[#ECFDF5] border border-gray-100 rounded-3xl p-8 md:p-10">
              {isSuccess ? (
                <div className="flex flex-col items-center justify-center py-12 text-center" data-testid="contact-success">
                  <div className="w-20 h-20 rounded-full bg-[#E8F5F3] flex items-center justify-center mb-6">
                    <CheckCircle size={40} className="text-[#10B981]" />
                  </div>
                  <h3 className="text-2xl font-bold text-[#1A1A1A] mb-4">Message envoyé !</h3>
                  <p className="text-[#1E3A5F] font-semibold max-w-md">
                    Merci pour votre message. Je vous recontacte dans les plus brefs délais.
                  </p>
                  <button
                    onClick={() => setIsSuccess(false)}
                    className="mt-8 text-[#10B981] font-medium hover:underline"
                    data-testid="send-another-btn"
                  >
                    Envoyer un autre message
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} data-testid="contact-form">
                  <h2 className="text-2xl font-bold text-[#1A1A1A] mb-8">Envoyez-moi un message</h2>

                  <div className="space-y-6">
                    {/* Name */}
                    <div>
                      <Label htmlFor="name" className="text-[#1A1A1A] mb-2 block font-medium">
                        Nom complet *
                      </Label>
                      <Input
                        id="name"
                        name="name"
                        type="text"
                        required
                        value={formData.name}
                        onChange={handleChange}
                        placeholder="Votre nom"
                        data-testid="contact-name-input"
                        className="bg-white border-gray-200 text-[#1A1A1A] placeholder:text-[#9CA3AF] focus:border-[#10B981] focus:ring-[#10B981] h-12 rounded-xl"
                      />
                    </div>

                    {/* Email */}
                    <div>
                      <Label htmlFor="email" className="text-[#1A1A1A] mb-2 block font-medium">
                        Email *
                      </Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        required
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="votre@email.com"
                        data-testid="contact-email-input"
                        className="bg-white border-gray-200 text-[#1A1A1A] placeholder:text-[#9CA3AF] focus:border-[#10B981] focus:ring-[#10B981] h-12 rounded-xl"
                      />
                    </div>

                    {/* Phone */}
                    <div>
                      <Label htmlFor="phone" className="text-[#1A1A1A] mb-2 block font-medium">
                        Téléphone *
                      </Label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        required
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="06 XX XX XX XX"
                        data-testid="contact-phone-input"
                        className="bg-white border-gray-200 text-[#1A1A1A] placeholder:text-[#9CA3AF] focus:border-[#10B981] focus:ring-[#10B981] h-12 rounded-xl"
                      />
                    </div>

                    {/* Message */}
                    <div>
                      <Label htmlFor="message" className="text-[#1A1A1A] mb-2 block font-medium">
                        Message (optionnel)
                      </Label>
                      <Textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        placeholder="Décrivez votre projet ou vos besoins..."
                        rows={5}
                        data-testid="contact-message-input"
                        className="bg-white border-gray-200 text-[#1A1A1A] placeholder:text-[#9CA3AF] focus:border-[#10B981] focus:ring-[#10B981] resize-none rounded-xl"
                      />
                    </div>

                    {/* Error Message */}
                    {error && (
                      <p className="text-red-500 text-sm" data-testid="contact-error">
                        {error}
                      </p>
                    )}

                    {/* Submit Button */}
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      data-testid="contact-submit-btn"
                      className="w-full inline-flex items-center justify-center gap-3 bg-[#10B981] text-white font-semibold rounded-full px-8 py-4 hover:bg-[#0D6B5E] transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isSubmitting ? (
                        <>
                          <Loader2 size={20} className="animate-spin" />
                          Envoi en cours...
                        </>
                      ) : (
                        <>
                          <Send size={20} />
                          Envoyer le message
                        </>
                      )}
                    </button>
                  </div>
                </form>
              )}
            </div>

            {/* Contact Info */}
            <div>
              <h2 className="text-2xl font-bold text-[#1A1A1A] mb-8">Mes coordonnées</h2>

              <div className="space-y-6 mb-12">
                {contactInfo.map((info, index) => (
                  <div
                    key={index}
                    data-testid={`contact-info-${index}`}
                    className="flex items-start gap-4"
                  >
                    <div className="w-12 h-12 rounded-xl bg-[#E8F5F3] flex items-center justify-center flex-shrink-0">
                      <info.icon size={24} className="text-[#10B981]" />
                    </div>
                    <div>
                      <p className="text-[#9CA3AF] text-sm mb-1">{info.label}</p>
                      {info.href ? (
                        <a
                          href={info.href}
                          className="text-[#1A1A1A] text-lg font-medium hover:text-[#10B981] transition-colors duration-200"
                        >
                          {info.value}
                        </a>
                      ) : (
                        <p className="text-[#1A1A1A] text-lg font-medium">{info.value}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Quick Contact Card */}
              <div className="bg-[#10B981] rounded-2xl p-8 text-white">
                <h3 className="text-xl font-bold mb-4">Besoin d'une réponse rapide ?</h3>
                <p className="text-white/80 mb-6">
                  Appelez-moi directement pour discuter de votre projet.
                </p>
                <a
                  href="tel:0658930595"
                  data-testid="quick-call-btn"
                  className="inline-flex items-center gap-3 bg-white text-[#10B981] font-semibold rounded-full px-6 py-3 hover:bg-gray-100 transition-all duration-200"
                >
                  <Phone size={20} />
                  06 58 93 05 95
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
